import {
  ActionIcon,
  Anchor,
  Badge,
  Button,
  Group,
  SegmentedControl,
  Select,
  Stack,
  Text,
  Textarea,
  TextInput,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useState } from 'react';
import { PiTrash } from 'react-icons/pi';
import { CollapsibleCard } from '@/components/CollapsibleCard';
import Link from '@/components/Link';
import { useAuth } from '@/models/auth';
import {
  ACTIVITY_KINDS,
  type ActivityKind,
  ChannelTakenError,
  type PersonCardResponse,
  useAddActivity,
  useAddChannel,
  useRemoveChannel,
  useUpdateActivity,
} from '@/models/persons';

const KIND_LABELS: Record<string, string> = {
  email: 'Email',
  phone: 'Phone',
  whatsapp: 'WhatsApp',
  telegram: 'Telegram',
};

export function ChannelsCard({ data }: { data: PersonCardResponse }) {
  const add = useAddChannel();
  const remove = useRemoveChannel();
  const [kind, setKind] = useState<string>('email');
  const [value, setValue] = useState('');
  const [taken, setTaken] = useState<ChannelTakenError | null>(null);
  const canEdit = data.access.canEdit;
  return (
    <CollapsibleCard title="Channels">
      <Stack gap={4}>
        {data.channels.map((c) => (
          <Group key={c.id} justify="space-between" wrap="nowrap">
            <Text size="sm">
              <Text span c="dimmed">
                {KIND_LABELS[c.kind]}:
              </Text>{' '}
              {c.value}{' '}
              {c.isPrimary && (
                <Badge size="xs" variant="light">
                  main
                </Badge>
              )}
            </Text>
            {canEdit && !c.isPrimary && (
              <ActionIcon
                size="sm"
                variant="subtle"
                color="red"
                aria-label="Remove channel"
                loading={remove.isPending}
                onClick={() => remove.mutate(c.id)}
              >
                <PiTrash />
              </ActionIcon>
            )}
          </Group>
        ))}
        {canEdit && (
          <Group gap="xs" align="flex-end" wrap="nowrap" mt={4}>
            <Select
              size="xs"
              w={110}
              data={Object.entries(KIND_LABELS).map(([v, label]) => ({
                value: v,
                label,
              }))}
              value={kind}
              onChange={(v) => setKind(v ?? 'email')}
            />
            <TextInput
              size="xs"
              placeholder="Add another channel"
              value={value}
              onChange={(e) => {
                setValue(e.currentTarget.value);
                setTaken(null);
              }}
              style={{ flex: 1 }}
            />
            <Button
              size="xs"
              disabled={!value.trim()}
              loading={add.isPending}
              onClick={async () => {
                try {
                  await add.mutateAsync({
                    contactId: data.person.id,
                    kind,
                    value,
                  });
                  setValue('');
                } catch (e) {
                  if (e instanceof ChannelTakenError) setTaken(e);
                  else throw e;
                }
              }}
            >
              Add
            </Button>
          </Group>
        )}
        {taken && (
          <Text size="xs" c="red">
            This email belongs to{' '}
            <Anchor
              component={Link}
              to={`~/contacts/${taken.contactId}`}
              fz="xs"
            >
              {taken.holderName ?? `#${taken.contactId}`}
            </Anchor>
          </Text>
        )}
      </Stack>
    </CollapsibleCard>
  );
}

export function TouchesCard({ data }: { data: PersonCardResponse }) {
  const { user } = useAuth();
  const add = useAddActivity();
  const update = useUpdateActivity();
  const [kind, setKind] = useState<ActivityKind>('call');
  const [status, setStatus] = useState<'done' | 'open'>('done');
  const [body, setBody] = useState('');
  const own = data.requests.filter(
    (r) => String(r.assigneeUserId) === String(user?.id),
  );
  const leadOptions = (data.access.parts.touches ? data.requests : own).map(
    (r) => ({ value: String(r.id), label: `#${r.id} ${r.status}` }),
  );
  const openLeads = data.requests.filter((r) => r.isOpen);
  const [leadId, setLeadId] = useState<string | null>(
    openLeads.length === 1 &&
      leadOptions.some((o) => o.value === String(openLeads[0].id))
      ? String(openLeads[0].id)
      : (leadOptions[0]?.value ?? null),
  );
  const mine = data.feed.activities.filter(
    (a) => a.status === 'open' && String(a.authorUserId) === String(user?.id),
  );
  if (!data.access.parts.touches && own.length === 0) return null;
  return (
    <CollapsibleCard title="Touches">
      <Stack gap="xs">
        <SegmentedControl
          size="xs"
          data={ACTIVITY_KINDS.map((k) => ({ value: k, label: k }))}
          value={kind}
          onChange={(v) => setKind(v as ActivityKind)}
        />
        <Group gap="xs" grow>
          <Select
            size="xs"
            placeholder="Request"
            data={leadOptions}
            value={leadId}
            onChange={setLeadId}
            clearable={data.access.parts.touches}
          />
          <SegmentedControl
            size="xs"
            data={[
              { value: 'done', label: 'Done' },
              { value: 'open', label: 'Planned' },
            ]}
            value={status}
            onChange={(v) => setStatus(v as 'done' | 'open')}
          />
        </Group>
        <Textarea
          size="xs"
          autosize
          minRows={2}
          placeholder="What happened"
          value={body}
          onChange={(e) => setBody(e.currentTarget.value)}
        />
        <Button
          size="xs"
          loading={add.isPending}
          disabled={!data.access.parts.touches && !leadId}
          onClick={async () => {
            await add.mutateAsync({
              contactId: data.person.id,
              leadId: leadId ? Number(leadId) : null,
              kind,
              status,
              body: body || null,
            });
            setBody('');
            notifications.show({ color: 'green', message: 'Touch saved' });
          }}
        >
          Save touch
        </Button>
        {mine.map((a) => (
          <Group key={a.id} justify="space-between" wrap="nowrap">
            <Text size="sm">
              {a.kind}: {a.body ?? ''}
            </Text>
            <Button
              size="compact-xs"
              variant="light"
              loading={update.isPending}
              onClick={() => update.mutate({ id: a.id, status: 'done' })}
            >
              Mark done
            </Button>
          </Group>
        ))}
      </Stack>
    </CollapsibleCard>
  );
}
